import { REGULATORY_LAST_REVIEWED } from '../../data/brsr/regulatorySources'
import {
  REGULATORY_CONFIG_VERSION,
  REGULATORY_RULES,
  coreAssuranceLimit,
  financialYearKey,
  financialYearLabel,
  isFyOnOrAfter,
  isWithinTop,
} from '../../data/brsr/regulatoryRules'
import type { AnswerMap, ApplicabilityAssessment, ApplicabilityConclusion, ConfidenceStatus } from '../../data/brsr/types'
import { responseLabel, scalarAnswer } from './labels'

function inputRows(responses: AnswerMap, ids: string[]): Array<{ label: string; value: string }> {
  const labels: Record<string, string> = {
    listing_status: 'Listing status',
    exchange: 'Exchange',
    listing_segment: 'Listing segment',
    cohort: 'Cohort range',
    cohort_basis: 'How the range was identified',
    financial_year: 'Financial year',
    supplies_listed: 'Supplies listed companies',
    customers_request_esg: 'Customers request ESG data',
  }
  return ids
    .filter((id) => scalarAnswer(responses, id) || id === 'financial_year')
    .map((id) => ({ label: labels[id] ?? id, value: responseLabel(id, responses) }))
}

function statusRank(status: ConfidenceStatus): number {
  if (status === 'requires_verification') return 2
  if (status === 'conditional') return 1
  return 0
}

export function evaluateApplicability(responses: AnswerMap): ApplicabilityAssessment {
  const fyAnswer = scalarAnswer(responses, 'financial_year')
  const fyKey = financialYearKey(fyAnswer)
  const fyLabel = financialYearLabel(fyAnswer)
  const listing = scalarAnswer(responses, 'listing_status')
  const segment = scalarAnswer(responses, 'listing_segment')
  const cohort = scalarAnswer(responses, 'cohort')
  const cohortBasis = scalarAnswer(responses, 'cohort_basis')
  const supplies = scalarAnswer(responses, 'supplies_listed')
  const customers = scalarAnswer(responses, 'customers_request_esg')

  const assumptions = [
    REGULATORY_RULES.rankingDisclaimer,
    'Conclusions use your answers and the regulatory configuration version ' +
      REGULATORY_CONFIG_VERSION +
      ', last reviewed ' +
      REGULATORY_LAST_REVIEWED +
      '.',
    REGULATORY_RULES.filingDeadline.note,
    REGULATORY_RULES.valueChainPartnerThreshold.note,
  ]

  const listingInputs = inputRows(responses, [
    'listing_status',
    'exchange',
    'listing_segment',
    'cohort',
    'cohort_basis',
    'financial_year',
  ])

  let filingLikely = false
  let coreDisclosureRelevant = false
  let coreAssessmentLikely = false
  let valueChainRelevant = false
  let valueChainBasis: ApplicabilityAssessment['valueChainBasis'] = 'not_indicated'
  let supplierPathway = supplies === 'yes' || customers === 'yes'

  const conclusions: ApplicabilityConclusion[] = []

  const segmentBlocks =
    listing === 'listed' && (segment === 'sme' || segment === 'debt_only' || segment === 'not_sure' || segment === '')

  if (!fyKey) {
    conclusions.push({
      id: 'filing',
      title: 'BRSR filing',
      conclusion: 'A filing conclusion needs a financial year.',
      reason: 'Applicability differs by reporting year. Select the year on the company profile.',
      inputs: listingInputs,
      financialYear: fyLabel,
      sourceIds: ['sebi-2021-brsr'],
      status: 'requires_verification',
      nextStep: 'Select the financial year being assessed and return to this screen.',
    })
  } else if (listing === 'unlisted') {
    conclusions.push({
      id: 'filing',
      title: 'BRSR filing',
      conclusion:
        'Your company may not have a direct BRSR filing obligation based on the information supplied, but customer-driven ESG data requirements may still apply.',
      reason:
        'The mandatory BRSR cohort is the top 1,000 listed entities by market capitalisation. An unlisted company is outside that direct filing rule. This is not a statement that no other law applies.',
      inputs: listingInputs,
      financialYear: fyLabel,
      sourceIds: ['sebi-2021-brsr'],
      status: 'verified_rule',
      nextStep: 'Use the later sections to see whether customer or voluntary reporting still needs organised data.',
    })
  } else if (listing !== 'listed' || segmentBlocks || !cohort || cohort === 'not_sure' || cohortBasis === 'not_sure') {
    const reason = segment === 'sme'
      ? REGULATORY_RULES.segmentCaveats.sme
      : segment === 'debt_only'
        ? REGULATORY_RULES.segmentCaveats.debt_only
        : listing !== 'listed'
          ? 'Listing status is missing or not sure, so the top-1,000 test cannot be applied.'
          : 'The market-capitalisation range is missing, marked not sure, or was not identified with enough confidence.'
    conclusions.push({
      id: 'filing',
      title: 'BRSR filing',
      conclusion: 'Further verification of your listing cohort is required before a filing conclusion can be drawn.',
      reason,
      inputs: listingInputs,
      financialYear: fyLabel,
      sourceIds: ['sebi-2021-brsr', 'sebi-circular-index'],
      status: 'requires_verification',
      nextStep: 'Confirm the equity listing segment and the market-capitalisation range on the relevant exchange list for the year.',
    })
  } else {
    const within1000 = isWithinTop(cohort, REGULATORY_RULES.brsrFiling.topListedEntities)
    const basisNote =
      cohortBasis === 'exchange_list'
        ? ' You indicated the range was checked against an exchange list. This tool did not repeat that check.'
        : cohortBasis === 'internal_estimate'
          ? ' You indicated the range is an internal estimate.'
          : ' This tool did not check an exchange ranking list.'
    if (within1000 === true) {
      filingLikely = true
      conclusions.push({
        id: 'filing',
        title: 'BRSR filing',
        conclusion:
          'Likely within the applicable reporting cohort based on your answers. This is not a SEBI determination.',
        reason:
          `For ${fyLabel}, mandatory BRSR applies to the top ${REGULATORY_RULES.brsrFiling.topListedEntities} listed entities by market capitalisation.` +
          basisNote,
        inputs: listingInputs,
        financialYear: fyLabel,
        sourceIds: ['sebi-2021-brsr'],
        status: 'conditional',
        nextStep: 'Keep the exchange ranking extract with the reporting file and complete the disclosure and evidence sections.',
      })
    } else {
      conclusions.push({
        id: 'filing',
        title: 'BRSR filing',
        conclusion:
          'Your company may not have a direct BRSR filing obligation based on the information supplied. Voluntary reporting may still be useful, and customer requests may still apply.',
        reason:
          `The selected range is outside the top ${REGULATORY_RULES.brsrFiling.topListedEntities}.` + basisNote,
        inputs: listingInputs,
        financialYear: fyLabel,
        sourceIds: ['sebi-2021-brsr'],
        status: 'conditional',
        nextStep: 'Confirm the ranking. If it is outside the top 1,000, decide whether a voluntary BRSR is still worth preparing.',
      })
    }
  }

  if (!fyKey || listing === '') {
    conclusions.push(blockedCore(fyLabel, listingInputs))
  } else if (listing === 'unlisted') {
    conclusions.push({
      id: 'core-disclosure',
      title: 'Updated BRSR format and BRSR Core KPIs',
      conclusion: 'A direct BRSR Core disclosure obligation is not indicated for an unlisted company from the information supplied.',
      reason: REGULATORY_RULES.updatedBrsrFormat.note + ' That format applies to the listed cohort, not to unlisted suppliers.',
      inputs: listingInputs,
      financialYear: fyLabel,
      sourceIds: ['sebi-2023-core'],
      status: 'verified_rule',
      nextStep: 'Core questions are hidden. Supplier data requests are covered under value chain if you said customers ask for ESG information.',
    })
    conclusions.push({
      id: 'core-assessment',
      title: 'BRSR Core assessment or assurance',
      conclusion: 'Mandatory BRSR Core assessment or assurance is not indicated for an unlisted company.',
      reason: 'The glide path applies to specified top listed entities, not to unlisted companies.',
      inputs: listingInputs,
      financialYear: fyLabel,
      sourceIds: ['sebi-2025-ease'],
      status: 'verified_rule',
      nextStep: 'Do not treat third-party assurance as automatically required. A customer may still ask for evidence.',
    })
  } else if (!filingLikely && conclusions.find((item) => item.id === 'filing')?.status === 'requires_verification') {
    conclusions.push({
      id: 'core-disclosure',
      title: 'Updated BRSR format and BRSR Core KPIs',
      conclusion: 'Whether the updated BRSR format applies still depends on the listing cohort.',
      reason: 'From FY 2023-24 the top 1,000 listed entities use the updated format. Your cohort is not confirmed.',
      inputs: listingInputs,
      financialYear: fyLabel,
      sourceIds: ['sebi-2023-core'],
      status: 'requires_verification',
      nextStep: 'Verify the cohort, then revisit Core data readiness.',
    })
    conclusions.push({
      id: 'core-assessment',
      title: 'BRSR Core assessment or assurance',
      conclusion: 'Further verification is required before saying whether assessment or assurance of BRSR Core is mandatory.',
      reason: 'The glide path depends on both the financial year and the market-capitalisation range.',
      inputs: listingInputs,
      financialYear: fyLabel,
      sourceIds: ['sebi-2025-ease'],
      status: 'requires_verification',
      nextStep: 'Compare the confirmed rank with the glide path for ' + fyLabel + '.',
    })
  } else if (listing === 'listed' && cohort && cohort !== 'not_sure' && !segmentBlocks) {
    const updatedFrom = isFyOnOrAfter(fyKey, REGULATORY_RULES.updatedBrsrFormat.fromFinancialYear)
    const inUpdatedCohort = isWithinTop(cohort, REGULATORY_RULES.updatedBrsrFormat.topListedEntities)
    if (updatedFrom === true && inUpdatedCohort === true) {
      coreDisclosureRelevant = true
      conclusions.push({
        id: 'core-disclosure',
        title: 'Updated BRSR format and BRSR Core KPIs',
        conclusion: 'Likely expected to disclose under the updated BRSR format, including BRSR Core KPIs, based on your answers.',
        reason: REGULATORY_RULES.updatedBrsrFormat.note,
        inputs: listingInputs,
        financialYear: fyLabel,
        sourceIds: ['sebi-2023-core'],
        status: 'conditional',
        nextStep: 'Complete the BRSR Core questions. Disclosure of the KPIs is separate from whether assessment or assurance is mandatory this year.',
      })
    } else {
      conclusions.push({
        id: 'core-disclosure',
        title: 'Updated BRSR format and BRSR Core KPIs',
        conclusion: 'The updated BRSR format is not indicated as mandatory for this cohort and year based on your answers.',
        reason: 'The updated format cited here applies to the top 1,000 from FY 2023-24.',
        inputs: listingInputs,
        financialYear: fyLabel,
        sourceIds: ['sebi-2023-core'],
        status: 'conditional',
        nextStep: 'You can still answer Core questions if you report voluntarily. They stay hidden when you are outside the top 1,000.',
      })
    }

    const limit = coreAssuranceLimit(fyKey)
    if (!limit) {
      conclusions.push({
        id: 'core-assessment',
        title: 'BRSR Core assessment or assurance',
        conclusion: 'This configuration has no glide-path row for the selected year.',
        reason: 'Do not infer a mandatory assessment from a neighbouring year.',
        inputs: listingInputs,
        financialYear: fyLabel,
        sourceIds: ['sebi-2025-ease', 'sebi-circular-index'],
        status: 'requires_verification',
        nextStep: 'Check the SEBI circular listing for an amendment covering ' + fyLabel + '.',
      })
    } else {
      const inGlide = isWithinTop(cohort, limit.topListedEntities)
      if (inGlide === true) {
        coreAssessmentLikely = true
        conclusions.push({
          id: 'core-assessment',
          title: 'BRSR Core assessment or assurance',
          conclusion:
            'Likely required to undertake assessment or assurance of BRSR Core for this year, based on your answers. This is not a confirmation of the engagement type.',
          reason: `The restated glide path for ${fyLabel} covers the top ${limit.topListedEntities} listed entities. The March 2025 circular uses “assessment or assurance”, not only the earlier “reasonable assurance” wording.`,
          inputs: listingInputs,
          financialYear: fyLabel,
          sourceIds: ['sebi-2025-ease', 'sebi-2023-core'],
          status: 'conditional',
          nextStep: 'Prepare an evidence file and confirm with the board whether the engagement will be an ISF assessment or an assurance.',
        })
      } else {
        conclusions.push({
          id: 'core-assessment',
          title: 'BRSR Core assessment or assurance',
          conclusion:
            'Mandatory assessment or assurance of BRSR Core is not indicated for this cohort in ' +
            fyLabel +
            '. KPI disclosure can still apply if you are inside the top 1,000.',
          reason: `For ${fyLabel} the glide path covers the top ${limit.topListedEntities} listed entities. Your selected range is outside that slice.`,
          inputs: listingInputs,
          financialYear: fyLabel,
          sourceIds: ['sebi-2025-ease'],
          status: 'conditional',
          nextStep: 'Do not assume an assurance opinion is required this year. Keep Core data ready if the updated format still applies.',
        })
      }
    }
  } else {
    conclusions.push(blockedCore(fyLabel, listingInputs))
  }

  const vcInputs = inputRows(responses, [
    'listing_status',
    'cohort',
    'financial_year',
    'supplies_listed',
    'customers_request_esg',
  ])

  if (!fyKey) {
    valueChainBasis = 'unknown'
    conclusions.push({
      id: 'value-chain',
      title: 'Value-chain ESG disclosures',
      conclusion: 'Value-chain classification needs a financial year.',
      reason: 'The March 2025 rules are year-specific.',
      inputs: vcInputs,
      financialYear: fyLabel,
      sourceIds: ['sebi-2025-ease'],
      status: 'requires_verification',
      nextStep: 'Select the financial year.',
    })
  } else {
    const vcYear = isFyOnOrAfter(fyKey, REGULATORY_RULES.valueChainDisclosures.fromFinancialYear)
    const inTop250 = listing === 'listed' ? isWithinTop(cohort, REGULATORY_RULES.valueChainDisclosures.topListedEntities) : false
    if (listing === 'listed' && (cohort === 'not_sure' || segmentBlocks || listing === 'listed' && !cohort)) {
      if (cohort === 'not_sure' || segmentBlocks) {
        valueChainBasis = 'unknown'
        valueChainRelevant = true
        conclusions.push({
          id: 'value-chain',
          title: 'Value-chain ESG disclosures',
          conclusion: 'Further verification is required before classifying SEBI value-chain disclosures.',
          reason:
            `From ${REGULATORY_RULES.valueChainDisclosures.fromFinancialYear} the disclosure is voluntary for the top ${REGULATORY_RULES.valueChainDisclosures.topListedEntities}. Your cohort is not confirmed. The July 2023 comply-or-explain timetable is not used.`,
          inputs: vcInputs,
          financialYear: fyLabel,
          sourceIds: ['sebi-2025-ease'],
          status: 'requires_verification',
          nextStep: 'Confirm whether the company is inside the top 250 for this year.',
        })
      }
    }
    if (vcYear === true && inTop250 === true && !segmentBlocks && cohort !== 'not_sure') {
      valueChainBasis = 'voluntary'
      valueChainRelevant = true
      const assessmentYear = isFyOnOrAfter(fyKey, REGULATORY_RULES.valueChainAssessmentOrAssurance.fromFinancialYear)
      conclusions.push({
        id: 'value-chain',
        title: 'Value-chain ESG disclosures',
        conclusion:
          'Value-chain ESG disclosure is voluntary for this cohort and year, not mandatory. It is kept separate from the BRSR filing conclusion.',
        reason: REGULATORY_RULES.valueChainDisclosures.note,
        inputs: vcInputs,
        financialYear: fyLabel,
        sourceIds: ['sebi-2025-ease'],
        status: 'conditional',
        nextStep:
          assessmentYear === true
            ? 'Assessment or assurance of value-chain disclosures is also voluntary for this year. Decide whether you will report, and keep previous-year comparison optional in the first reporting year.'
            : 'Assessment or assurance of value-chain disclosures is not yet on the voluntary timetable for this year. Disclosure itself is voluntary.',
      })
    } else if (vcYear !== true && listing === 'listed' && inTop250 === true && !segmentBlocks) {
      conclusions.push({
        id: 'value-chain',
        title: 'Value-chain ESG disclosures',
        conclusion:
          'SEBI value-chain ESG disclosure is not indicated for ' +
          fyLabel +
          '. The current voluntary window starts in FY 2025-26.',
        reason:
          'The March 2025 circular deferred value-chain disclosure and made it voluntary from FY 2025-26 for the top 250. Do not apply the July 2023 comply-or-explain date.',
        inputs: vcInputs,
        financialYear: fyLabel,
        sourceIds: ['sebi-2025-ease'],
        status: 'conditional',
        nextStep: 'You may still prepare supplier data if customers ask. That preparation is not a filing obligation for this year.',
      })
    } else if (!(cohort === 'not_sure' || segmentBlocks)) {
      conclusions.push({
        id: 'value-chain',
        title: 'Value-chain ESG disclosures',
        conclusion:
          listing === 'unlisted'
            ? 'A SEBI value-chain filing is not indicated. Customer-driven ESG data requests are assessed separately.'
            : 'The company is not in the top-250 voluntary value-chain cohort based on your answers.',
        reason: REGULATORY_RULES.valueChainDisclosures.note,
        inputs: vcInputs,
        financialYear: fyLabel,
        sourceIds: ['sebi-2025-ease'],
        status: listing === 'unlisted' ? 'verified_rule' : 'conditional',
        nextStep: 'Treat customer questionnaires as a commercial data need, not as a BRSR filing trigger.',
      })
    }
  }

  if (supplies === 'yes' || customers === 'yes' || supplies === 'not_sure' || customers === 'not_sure') {
    valueChainRelevant = true
    if (valueChainBasis === 'not_indicated') {
      valueChainBasis = supplies === 'not_sure' || customers === 'not_sure' ? 'unknown' : 'customer_driven'
    }
    conclusions.push({
      id: 'supplier-pathway',
      title: 'Customer and supplier data requests',
      conclusion:
        supplies === 'not_sure' || customers === 'not_sure'
          ? 'Customer-driven ESG data needs require verification.'
          : 'Customer-driven ESG data requirements may apply even where there is no direct BRSR filing obligation.',
      reason:
        'Supplying listed customers, or receiving ESG questionnaires, is a readiness issue. It does not by itself place the company in the SEBI filing cohort.',
      inputs: vcInputs,
      financialYear: fyLabel,
      sourceIds: ['sebi-2025-ease'],
      status: supplies === 'not_sure' || customers === 'not_sure' ? 'requires_verification' : 'conditional',
      nextStep: 'Document what each customer asks for and which of those metrics you can support with evidence.',
    })
  }

  conclusions.push({
    id: 'green-credits',
    title: 'Green credits',
    conclusion: 'Disclosure of green credits is a voluntary leadership indicator, not a mandatory BRSR line.',
    reason: REGULATORY_RULES.greenCredits.note,
    inputs: [{ label: 'Financial year', value: fyLabel }],
    financialYear: fyLabel,
    sourceIds: ['sebi-2025-ease'],
    status: 'verified_rule',
    nextStep: 'Report green credits only if you generate or procure them and choose to disclose the leadership indicator.',
  })

  const primary = conclusions.filter((item) => item.id === 'filing' || item.id === 'core-assessment')
  const overallStatus = primary.reduce<ConfidenceStatus>((current, item) => {
    return statusRank(item.status) > statusRank(current) ? item.status : current
  }, 'verified_rule')

  return {
    regulatoryConfigVersion: REGULATORY_CONFIG_VERSION,
    financialYearLabel: fyLabel,
    conclusions,
    filingLikely,
    coreDisclosureRelevant,
    coreAssessmentLikely,
    valueChainRelevant,
    valueChainBasis,
    supplierPathway,
    overallStatus,
    assumptions,
  }
}

function blockedCore(fyLabel: string, inputs: Array<{ label: string; value: string }>): ApplicabilityConclusion {
  return {
    id: 'core-disclosure',
    title: 'BRSR Core',
    conclusion: 'BRSR Core applicability cannot be concluded until listing and year are clear.',
    reason: 'Core disclosure and the assessment glide path both depend on the listed cohort and the financial year.',
    inputs,
    financialYear: fyLabel,
    sourceIds: ['sebi-2023-core', 'sebi-2025-ease'],
    status: 'requires_verification',
    nextStep: 'Complete listing status, segment, cohort and financial year.',
  }
}
